import { Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, downloadExtension } from '../../lib/api';
import { fileSize } from '../../lib/samples';
import { useParent } from '../ParentContext';

/**
 * The browser extension's package for the stores (#640). Families only get the extension, and its updates, from the
 * Chrome Web Store and Edge Add-ons, so the package built into this server is for the admin to submit there. The
 * versions families' browsers report show a store update rolling out.
 */
interface ExtensionInfo {
  package: { version: string; file: string; size: number } | null;
  inUse: { version: string; n: number }[];
}

export function ExtensionPackage() {
  const { toast } = useParent();
  const [info, setInfo] = useState<ExtensionInfo | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get<ExtensionInfo>('/api/admin/extension')
      .then(setInfo)
      .catch(() => setInfo(null));
  }, []);
  if (!info) return null;
  const pkg = info.package;
  async function download() {
    if (!pkg) return;
    setBusy(true);
    try {
      await downloadExtension(pkg.version);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack ext-package">
      <strong>Package for the stores</strong>
      {pkg ? (
        <div className="row-between wrap ext-package-file">
          <span>
            <strong>{pkg.file}</strong>
            <span className="muted small block">
              v{pkg.version} · built into this server · {fileSize(pkg.size)}
            </span>
          </span>
          <button className="btn primary small" onClick={download} disabled={busy}>
            <Download size={15} /> {busy ? 'Downloading…' : 'Download for the stores'}
          </button>
        </div>
      ) : (
        <p className="muted small">No extension package is built into this server.</p>
      )}
      <ol className="small ext-steps">
        <li>Chrome Web Store Developer Dashboard → ClassGuardian → Package → Upload new package.</li>
        <li>Edge Partner Center → ClassGuardian → Packages → replace the package.</li>
        <li>Submit both for review. Once approved, browsers update on their own within a few hours.</li>
      </ol>
      <div className="stack tight">
        <strong className="small">In families’ browsers</strong>
        {!info.inUse.length && <span className="muted small">No browser has paired the extension yet.</span>}
        {info.inUse.map((v) => (
          <span key={v.version || 'unknown'} className="row-between small">
            <span>
              {v.version ? `v${v.version}` : 'Version not reported'}
              {pkg && v.version === pkg.version ? ' (this package)' : ''}
            </span>
            <span className={pkg && v.version && v.version !== pkg.version ? 'warn-text' : 'muted'}>
              {v.n} browser{v.n === 1 ? '' : 's'}
              {pkg && v.version && v.version !== pkg.version ? ' · waiting for the store' : ''}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
